
































































































































import * as R from 'ramda'
import axios from 'axios'
import moment from 'moment-timezone'
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { ModuleNames } from '@/store/module-names'
import { ActionNames, GetterNames } from '@/store/modules/exercise-program/keys'
import { ActionNames as PatientExerciseActionNames } from '@/store/modules/exercise/keys'
import { UserActionContexts } from '@/constants/urls'
import { PatientExerciseTypes } from '@/jsonapi/PatientExercise'
import { EMPTY_STRENGTHENING_EXERCISE, EMPTY_CARDIO_EXERCISE } from '@/store/modules/exercise/constants'
import ExerciseProgramSessionNewPatientExercise from '@/components/exercise-programs/forms/ExerciseProgramSessionNewPatientExercise.vue'
import ExerciseProgramSessionEditPatientExercise from '@/components/exercise-programs/forms/ExerciseProgramSessionEditPatientExercise.vue'
import ExerciseProgramDatePickerWrapper from '@/components/exercise-programs/forms/ExerciseProgramDatePickerWrapper.vue'

//Draggable exercise card functionality
import draggable from 'vuedraggable'

type ComponentData = {
  isFetchingExercisePrograms: boolean
  isSavingData: boolean
  showExerciseSessionDetailsSaved: boolean
  exerciseSessionId: string | null
  exerciseSessionDate: string
  exerciseSessionExercises: any[]
  invalidExercises: string[]
}

export default Vue.extend({
  components: {
    ExerciseProgramSessionNewPatientExercise,
    ExerciseProgramSessionEditPatientExercise,
    ExerciseProgramDatePickerWrapper,
    draggable,
  },

  data(): ComponentData {
    return {
      isFetchingExercisePrograms: true,
      isSavingData: false,
      showExerciseSessionDetailsSaved: false,
      exerciseSessionId: null,
      exerciseSessionExercises: [],
      invalidExercises: [],
      exerciseSessionDate: '',
    }
  },

  computed: {
    showLoading() {
      return this.isFetchingExercisePrograms || this.isSavingData
    },
    userActionContextIsNew() {
      return this.$route.params.context === UserActionContexts.New
    },
    userActionContextIsEdit() {
      return this.$route.params.context === UserActionContexts.Edit
    },
    hasInvalidExercises() {
      return this.invalidExercises.length > 0
    },
    exerciseProgram() {
      return this[GetterNames.chosenExerciseProgram]
    },
    exerciseSession() {
      if (this.userActionContextIsEdit) {
        return this.exerciseProgram.sessions.find(session => session.id === this.exerciseSessionId)
      }
      return false
    },
    exerciseSessionExercisesByCurrentIndex() {
      return this.exerciseSessionExercises.map((exercise, index) => ({
        ...exercise,
        order: index,
      }))
    },
    exerciseSessionStrengtheningExercises() {
      return this.exerciseSessionExercisesByCurrentIndex.filter(
        exercise => exercise.type === PatientExerciseTypes.Strength
      )
    },
    exerciseSessionCardioExercises() {
      return this.exerciseSessionExercisesByCurrentIndex.filter(
        exercise => exercise.type === PatientExerciseTypes.Cardio
      )
    },
    exerciseSessionIsInProgress() {
      if (this.exerciseSession) {
        return this.exerciseSession.startTimestamp && !this.exerciseSession.endTimestamp ? true : false
      }
      return false
    },
    exerciseSessionIsComplete() {
      if (this.exerciseSession) {
        return this.exerciseSession.startTimestamp && this.exerciseSession.endTimestamp ? true : false
      }
      return false
    },
    pageHeaderText() {
      if (this.exerciseSessionIsComplete) {
        return `This session was completed at ${moment
          .unix(this.exerciseSession.endTimestamp)
          .format('HH:mm on dddd Do MMMM, YYYY')}`
      } else if (this.exerciseSessionIsInProgress) {
        return `Let's edit this session in progress:`
      } else {
        return `First, let's define today's exercise session details:`
      }
    },
    liveSessionLinkText() {
      if (this.exerciseSessionIsInProgress) {
        return `Resume session`
      } else if (this.userActionContextIsEdit) {
        return `Start live session`
      }
      return `Skip and edit later`
    },
    liveSessionLinkParams() {
      return {
        name: 'exercise-program-live-exercise-session',
        params: {
          program_id: this.exerciseProgram.id,
          exercise_session_id: this.exerciseSession.id,
        },
      }
    },
    sessionDetailsSavedText() {
      if (this.exerciseSessionIsComplete) {
        return 'Close'
      } else {
        return this.exerciseSessionIsInProgress ? 'Resume live session' : 'Start live session'
      }
    },
    defaultExerciseProgramExercises() {
      return R.concat(this.exerciseProgram.cardioExercises, this.exerciseProgram.strengtheningExercises)
    },
    ...mapGetters(ModuleNames.exerciseProgram, [
      GetterNames.exercisePrograms,
      GetterNames.chosenExerciseProgram,
      GetterNames.chosenExerciseProgramId,
    ]),
  },

  async mounted() {
    //fetch programs in case of page refresh
    if (!this[GetterNames.chosenExerciseProgramId]) {
      await this[ActionNames.getExercisePrograms]()
      this[ActionNames.setChosenExerciseProgramId](this.$route.params.program_id)
    }
    if (this.userActionContextIsEdit) {
      this.initializeExistingExerciseSession()
    } else {
      this.exerciseSessionDate = moment().format('YYYY-MM-DD')
      this.exerciseSessionExercises = this.defaultExerciseProgramExercises
    }
    /*
    patientExercises currently lack a unique id, and do not have a name
    when newly created. Therefore it's necessary to assign existing
    exercises an id on mount, and new exercises an id when created.
    The id is used to set :key in the ExerciseProgramSessionEditPatientExercise component, and to locate
    patientExercises in the parent array when editing/deleting.
    */
    this.exerciseSessionExercises = this.exerciseSessionExercises
      .map(exercise => ({
        ...exercise,
        id: `${exercise.type}-${this.generateTemporaryId()}`,
      }))
      .sort((a, b) => {
        return a.order > b.order ? 1 : -1
      })
    this.isFetchingExercisePrograms = false
  },

  methods: {
    initializeExistingExerciseSession() {
      this.exerciseSessionId = this.$route.params.exercise_session_id
      const exerciseSessionIndex = R.findIndex(R.propEq('id', this.exerciseSessionId))(this.exerciseProgram.sessions)
      const combinedExercises = [
        ...this.exerciseProgram.sessions[exerciseSessionIndex].cardioExercises,
        ...this.exerciseProgram.sessions[exerciseSessionIndex].strengtheningExercises,
      ]
      this.exerciseSessionExercises = combinedExercises.sort((a, b) => {
        return a.order > b.order ? 1 : -1
      })
      this.exerciseSessionDate = this.exerciseProgram.sessions[exerciseSessionIndex].date
      if (this.$route.query.just_created) {
        this.showExerciseSessionDetailsSaved = true
      }
    },
    generateTemporaryId() {
      return (Math.random() + 1).toString(36).substring(7)
    },
    getTargetExerciseIndex(exercise) {
      return R.findIndex(R.propEq('id', exercise.id))(this.exerciseSessionExercises)
    },
    handleNewPatientExercise(e) {
      if (e === PatientExerciseTypes.Strength) {
        this.exerciseSessionExercises.push({
          ...EMPTY_STRENGTHENING_EXERCISE,
          id: `${e}-${this.generateTemporaryId()}`,
        })
      } else if (e === PatientExerciseTypes.Cardio) {
        this.exerciseSessionExercises.push({
          ...EMPTY_CARDIO_EXERCISE,
          id: `${e}-${this.generateTemporaryId()}`,
        })
      }
    },
    handleUpdatePatientExercise(e) {
      this.exerciseSessionExercises[this.getTargetExerciseIndex(e)] = e
    },
    handleValidatePatientExercise(e) {
      if (!e.id) {
        return
      }
      const targetIndex = this.invalidExercises.indexOf(e.id)
      if (e.isValid && targetIndex >= 0) {
        this.invalidExercises.splice(targetIndex, 1)
        return
      }
      if (!e.isValid && targetIndex === -1) {
        this.invalidExercises.push(e.id)
      }
    },
    handleDeletePatientExercise(e) {
      const targetIndex = this.getTargetExerciseIndex(e)
      if (targetIndex !== -1) {
        this.exerciseSessionExercises.splice(this.getTargetExerciseIndex(e), 1)
      }
    },
    async saveExerciseSessionDetails() {
      if (this.hasInvalidExercises) {
        return
      }
      this.isSavingData = true
      if (this.userActionContextIsNew) {
        await this.postNewExerciseSession()
      } else if (this.userActionContextIsEdit) {
        await this.patchExerciseSession()
        await this.patchPatientExercises()
      }
      if (!this.exerciseSessionIsComplete) {
        await this.patchExerciseProgram()
      }
      this.isSavingData = false
    },
    async startLiveExerciseSession() {
      if (this.hasInvalidExercises) {
        return
      }
      this.isSavingData = true
      if (this.userActionContextIsNew) {
        await this.postNewExerciseSession()
      } else if (this.userActionContextIsEdit) {
        await this.patchExerciseSession()
        await this.patchPatientExercises()
      }
      this.isSavingData = false
      this.$router.push(this.liveSessionLinkParams)
    },
    sessionDetailsSavedAction() {
      if (this.exerciseSessionIsComplete) {
        this.showExerciseSessionDetailsSaved = false
      } else {
        this.startLiveExerciseSession()
      }
    },
    returnToProgramOverviewAction() {
      this.$router.push({ name: 'exercise-programs' })
    },
    closeExerciseSessionDetailsSavedDialog() {
      this.showExerciseSessionDetailsSaved = false
    },
    async postNewExerciseSession() {
      //create exercise session record
      const newExerciseSessionId = await this[ActionNames.postExerciseProgramSession]({
        programId: this.exerciseProgram.id,
        date: this.exerciseSessionDate,
        participants: this.exerciseProgram.participants,
        strengtheningExercises: this.exerciseSessionStrengtheningExercises,
        cardioExercises: this.exerciseSessionCardioExercises,
      })
      this.exerciseSessionId = newExerciseSessionId
      //create individual patient exercise records
      const patientExerciseIds = []
      const patientExercisePostArray = []
      this.exerciseProgram.participants.forEach(participantId => {
        patientExercisePostArray.push(
          this[PatientExerciseActionNames.postExercise]({
            patientId: participantId,
            exerciseDate: this.exerciseSessionDate,
            strengtheningExercises: this.exerciseSessionStrengtheningExercises,
            cardioExercises: this.exerciseSessionCardioExercises,
          })
            .then(res => {
              patientExerciseIds.push({
                patientId: participantId,
                exerciseId: res,
              })
            })
            .catch(err => {
              console.log(`failed to create patient-exercise record for patientId ${participantId}`)
              console.log(err)
            })
        )
      })
      console.log(`before post ${this.exerciseProgram.participants.length} patient-exercise records`)
      await axios.all(patientExercisePostArray)
      //patch session record with patientExerciseIds
      console.log(patientExerciseIds)
      await this[ActionNames.patchExerciseProgramSession]({
        id: this.exerciseSessionId,
        attributes: {
          patientExerciseIds: patientExerciseIds,
        },
      })
      //push to EDIT route
      this.$router.push({
        name: 'edit-exercise-session',
        params: {
          context: UserActionContexts.Edit,
          program_id: this[GetterNames.chosenExerciseProgramId],
          exercise_session_id: newExerciseSessionId,
        },
        query: {
          just_created: true,
        },
      })
      this.showExerciseSessionDetailsSaved = true
    },
    async patchExerciseSession() {
      await this[ActionNames.patchExerciseProgramSession]({
        id: this.exerciseSessionId,
        attributes: {
          date: this.exerciseSessionDate,
          participants: this.exerciseProgram.participants,
          strengtheningExercises: this.exerciseSessionStrengtheningExercises,
          cardioExercises: this.exerciseSessionCardioExercises,
        },
      })
      this.showExerciseSessionDetailsSaved = true
    },
    async patchPatientExercises() {
      if (!this.exerciseSession.patientExerciseIds) return
      const patientExercisePatchArray = []
      this.exerciseSession.patientExerciseIds.forEach(ref => {
        patientExercisePatchArray.push(
          this[PatientExerciseActionNames.patchExercise]({
            id: ref.exerciseId,
            attributes: {
              exerciseDate: this.exerciseSessionDate,
              strengtheningExercises: this.exerciseSessionStrengtheningExercises,
              cardioExercises: this.exerciseSessionCardioExercises,
            },
          }).catch(err => {
            console.log(`failed to patch patient-exercise ${err}`)
          })
        )
      })
      console.log(`before patch ${patientExercisePatchArray.length} patient-exercise records`)
      await axios.all(patientExercisePatchArray)
    },
    async patchExerciseProgram() {
      try {
        await this[ActionNames.patchExerciseProgram]({
          id: this.exerciseProgram.id,
          attributes: {
            cardioExercises: this.exerciseSessionCardioExercises,
            strengtheningExercises: this.exerciseSessionStrengtheningExercises,
          },
        })
      } catch (err) {
        console.log(err)
      }
    },
    ...mapActions(ModuleNames.exerciseProgram, [
      ActionNames.getExercisePrograms,
      ActionNames.setChosenExerciseProgramId,
      ActionNames.postExerciseProgramSession,
      ActionNames.patchExerciseProgramSession,
      ActionNames.patchExerciseProgram,
    ]),
    ...mapActions(ModuleNames.exercise, [
      PatientExerciseActionNames.patchExercise,
      PatientExerciseActionNames.postExercise,
    ]),
  },
})
